import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Actions from "../../../components/Common/Actions";
import AntdBreadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../../components/Common/TabBar";
import ListPageLayout from "../../../components/Layout";

const DoctorInclinicAppointment = () => {
  const navigate = useNavigate();
  const addNewClinicProfile = {
    id: "addnewpost",
    onClick: () => navigate("/add-new-inclinic-form"),
    style: { width: "240px" },
    label: "Add New Clinic/ Hospital Profile",
  };

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortName, setSortName] = useState("Newest First");

  // Full Data
  const fullData = [
    {
      sno: "1",
      name: "Anuj K",
      spl: "Andrology",
      clinic: "careline Fertility Clinic, Wonderkids Healthcare Hospitals,",
      fee: "399",
      languages: ["English", "Hindi", "Kannada"],
      exp: "6",
      profile: "",
      mobile: "+91 98654 23856",
      email: "anuj.k@gmail.com",
      time: "",
      mrn: "KA242196421",
      location: "Bangalore",
      state: "Karnataka",
    },
    {
      sno: "2",
      name: "Ankil G",
      spl: "Genereal Physician",
      clinic: "Medspa Clinic, Goodwell Hospital",
      fee: "299",
      languages: ["English", "Marathi"],
      exp: "3",
      profile: "",
      mobile: "+91 98654 23856",
      email: "ankil.g@gmail.com",
      time: "",
      mrn: "MH942R2414",
      location: "Mumbai",
      state: "Maharastra",
    },
  ];

  // SEARCH + SORT + PAGINATION
  const filteredSortedData = useMemo(() => {
    let temp = [...fullData];

    // Search
    if (search) {
      const lowerSearch = search.toLowerCase();
      temp = temp.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(lowerSearch),
        ),
      );
    }

    // Sort
    switch (sort) {
      case "newest":
        temp.sort((a, b) => b.sno.localeCompare(a.sno));
        break;
      case "oldest":
        temp.sort((a, b) => a.sno.localeCompare(b.sno));
        break;
      case "ascending":
        temp.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "descending":
        temp.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }

    return temp;
  }, [search, sort]);

  const totalItems = filteredSortedData.length;
  const totalPages = Math.ceil(totalItems / limit);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * limit;
    const end = start + limit;
    return filteredSortedData.slice(start, end);
  }, [filteredSortedData, currentPage, limit]);

  const handleSortClick = (selectedSort) => {
    setSort(selectedSort);
    setSortName(
      {
        newest: "Newest First",
        oldest: "Oldest First",
        ascending: "Alphabetic A-Z",
        descending: "Alphabetic Z-A",
      }[selectedSort] || "Sort by",
    );
  };

  const debouncedSearch = debounce((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, 500);

  const onChangeSearch = (value) => debouncedSearch(value);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleEdit = () => {
    navigate("/edit-inclinic-form");
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      render: (text, record, index) => Number(0) + (index + 1),
      width: 80,
    },
    {
      title: "Name of Doctors",
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: "Specialisation",
      dataIndex: "spl",
      key: "spl",
      width: 150,
    },
    {
      title: "Clinic / Hospital",
      dataIndex: "clinic",
      key: "clinic",
      width: 200,
    },
    {
      title: "In-Clinic Appointment Fees (₹INR)",
      dataIndex: "fee",
      key: "fee",
      width: 150,
    },
    {
      title: "Languages Known",
      dataIndex: "languages",
      key: "languages",
      render: (langs) => (Array.isArray(langs) ? langs.join(", ") : langs),
      width: 150,
    },
    {
      title: "Experience in Yrs",
      dataIndex: "exp",
      key: "exp",
      width: 100,
    },
    {
      title: "Profile Picture",
      dataIndex: "profile",
      key: "profile",
      render: (url) => (
        <img
          src={url}
          alt="Profile"
          style={{ width: 40, height: 40, borderRadius: "50%" }}
        />
      ),
      width: 100,
    },
    {
      title: "Registered Mobile Number",
      dataIndex: "mobile",
      key: "mobile",
      width: 150,
    },
    {
      title: "Registered Email",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Timings",
      key: "timings",
      render: () => (
        <span className="doctor-view" style={{ cursor: "pointer" }}>
          View
        </span>
      ),
      width: 100,
    },
    {
      title: "Medical Registration Number",
      dataIndex: "mrn",
      key: "mrn",
      width: 150,
    },
    {
      title: "City / Town",
      dataIndex: "location",
      key: "location",
      width: 150,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 150,
    },
    {
      title: "Actions",
      key: "actions",
      width: 300,
      render: (_, record) => (
        <Actions
          isEdit={true}
          isDelete={true}
          isView={true}
          onClickView={() => navigate("/view-inclinic-form")}
          onClickEdit={() => handleEdit(record)}
        />
      ),
    },
  ];

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctor",
        link: "/add-new-doctors",
      },
      { text: "Clinic & Hospital", link: "/add-new-clinic" },
      { text: "Add New Clinic & Hospital", link: "" },
    ],
    [],
  );

  const healthArticleNavData = [
    {
      name: "Active Clinic & Hospitals",
      navigatePath: "/add-new-clinic",
    },
    {
      name: "Doctors in In-clinic Appointments",
      navigatePath: "/doctor-inclinic-appointment",
    },
  ];

  return (
    <ListPageLayout
      breadcrumbs={<AntdBreadcrumbs breadcrumbsList={breadcrumbsList} />}
      addButton={<FilledButton props={addNewClinicProfile} />}
      titles={<NavBarTitles titles={healthArticleNavData} />}
      totalCount={totalItems}
      pagination={{
        currentPage,
        skip: (currentPage - 1) * limit,
        limit,
        totalPages,
        onPageChange: handlePageChange,
      }}
      filters={
        <DoctorsFilterOptions
          limit={limit}
          setLimit={setLimit}
          search={search}
          onChange={onChangeSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource: paginatedData,
        bordered: true,
        scroll: { x: "max-content" },
        className: "mx-3 pt-2 table-scrollable masterList_table",
        pagination: false,
      }}
    />
  );
};

export default DoctorInclinicAppointment;
